import React from "react";
import { Link } from "react-router-dom";
import zrsLogo from "@/brand/zrsLogo";
import { useI18n } from "@/i18n/I18nProvider";
import {
    Backpack,
    Pickaxe,
    Crosshair,
    Rocket,
    Crown,
} from "lucide-react";

// Mesmos ícones que os kits (Lucide) — repetidos para preencher o fundo
const KIT_HERO_ICONS = [Backpack, Pickaxe, Crosshair, Rocket, Crown];

// Hand-placed background icons — not a generic grid
const BG_ICON_LAYOUT = [
    { top: "12%", left: "6%",  size: 70, delay: "0s",   rot: -8,  op: 0.07 },
    { top: "22%", left: "86%", size: 86, delay: "1.2s", rot: 6,   op: 0.08 },
    { top: "68%", left: "8%",  size: 64, delay: "0.6s", rot: 8,   op: 0.07 },
    { top: "78%", left: "82%", size: 90, delay: "2s",   rot: -6,  op: 0.08 },
    { top: "40%", left: "3%",  size: 56, delay: "3s",   rot: 10,  op: 0.06 },
    { top: "55%", left: "92%", size: 70, delay: "1.8s", rot: -4,  op: 0.07 },
    { top: "85%", left: "38%", size: 80, delay: "2.4s", rot: -10, op: 0.06 },
    { top: "8%",  left: "58%", size: 58, delay: "0.9s", rot: 4,   op: 0.05 },
    { top: "50%", left: "50%", size: 120, delay: "0s",  rot: 0,   op: 0.035 },
    { top: "30%", left: "72%", size: 60, delay: "1.4s", rot: 14,  op: 0.06 },
    { top: "72%", left: "62%", size: 60, delay: "2.7s", rot: -12, op: 0.06 },
    { top: "18%", left: "28%", size: 52, delay: "1.1s", rot: 5,   op: 0.05 },
    { top: "62%", left: "28%", size: 58, delay: "0.3s", rot: -8,  op: 0.055 },
    { top: "36%", left: "94%", size: 50, delay: "3.4s", rot: 8,   op: 0.05 },
    { top: "14%", left: "44%", size: 54, delay: "2.1s", rot: -6,  op: 0.05 },
    { top: "90%", left: "14%", size: 50, delay: "1.5s", rot: 6,   op: 0.05 },
];

const BG_ICONS = BG_ICON_LAYOUT.map((layout, i) => ({
    ...layout,
    Icon: KIT_HERO_ICONS[i % KIT_HERO_ICONS.length],
}));

export default function Hero({ discordUrl }) {
    const { t } = useI18n();
    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative min-h-[100vh] pt-32 md:pt-40 pb-28 md:pb-32 flex items-center overflow-hidden"
            style={{
                background:
                    "radial-gradient(ellipse 60% 50% at 50% 40%, #1A0F0A 0%, #050505 75%)",
            }}
        >
            {/* Floating background — ícones dos kits (Lucide) */}
            <div className="absolute inset-0 pointer-events-none z-0">
                {BG_ICONS.map(({ Icon, top, left, size, delay, rot, op }, i) => (
                    <div
                        key={i}
                        className={`absolute ${i % 2 === 0 ? "float-slow" : "float-drift"}`}
                        style={{
                            top, left,
                            animationDelay: delay,
                            "--rot": `${rot}deg`,
                            "--base-op": op,
                            color: "#FFB800",
                            opacity: op,
                        }}
                    >
                        <Icon
                            size={size}
                            strokeWidth={size >= 90 ? 1 : 1.2}
                            aria-hidden
                        />
                    </div>
                ))}
            </div>

            {/* Gold radial glow behind logo */}
            <div
                className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none z-0 gold-pulse"
                style={{
                    background:
                        "radial-gradient(circle, rgba(255,184,0,0.26) 0%, rgba(255,184,0,0.08) 35%, transparent 65%)",
                    filter: "blur(20px)",
                }}
            />

            {/* Noise grain layer (section local) */}
            <div
                className="absolute inset-0 pointer-events-none z-0 opacity-[0.05] mix-blend-overlay"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                }}
            />

            <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-8 text-center">
                {/* Small tag */}
                <div
                    className="fade-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 glass text-[11px] uppercase tracking-[0.3em] text-zrs-metal mb-8"
                    style={{ animationDelay: "0.05s" }}
                    data-testid="hero-tagline"
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold gold-pulse" />
                    {t("hero.tagline")}
                </div>

                {/* Logo */}
                <div
                    className="fade-up flex justify-center mb-6 md:mb-8"
                    style={{ animationDelay: "0.1s" }}
                >
                    <img
                        src={zrsLogo}
                        alt={t("hero.logoAlt")}
                        data-testid="hero-logo"
                        className="logo-reveal w-36 h-36 md:w-48 md:h-48 object-contain select-none"
                        draggable={false}
                        style={{
                            animationDelay: "0.15s",
                            filter:
                                "drop-shadow(0 0 40px rgba(255,184,0,0.55)) drop-shadow(0 0 90px rgba(255,184,0,0.3))",
                        }}
                    />
                </div>

                {/* Title */}
                <h1
                    data-testid="hero-title"
                    className="fade-up font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-zrs-offwhite"
                    style={{ animationDelay: "0.3s" }}
                >
                    {t("hero.titleStart")}{" "}
                    <span className="text-gold gold-glow-text">{t("hero.titleHighlight")}</span>{" "}
                    <br className="hidden sm:block" />
                    {t("hero.titleEnd")}
                </h1>

                {/* Subtitle */}
                <p
                    data-testid="hero-subtitle"
                    className="fade-up mt-6 text-base md:text-lg text-zrs-offwhite/70 max-w-2xl mx-auto leading-relaxed"
                    style={{ animationDelay: "0.45s" }}
                >
                    {t("hero.subtitle")}
                </p>

                {/* CTAs — staggered individually */}
                <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                        to="/#vips"
                        data-testid="hero-cta-vips"
                        className="fade-up btn-gold btn-shimmer w-full sm:w-auto px-6 py-3 rounded-full font-bold text-[14px] tracking-wide"
                        style={{ animationDelay: "0.65s" }}
                    >
                        {t("hero.ctaVips")}
                    </Link>
                    <Link
                        to="/#kits"
                        data-testid="hero-cta-kits"
                        className="fade-up btn-outline-gold w-full sm:w-auto px-6 py-3 rounded-full font-bold text-[14px] tracking-wide"
                        style={{ animationDelay: "0.78s" }}
                    >
                        {t("hero.ctaKits")}
                    </Link>
                    <a
                        href={discordUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-cta-discord"
                        className="fade-up btn-ghost w-full sm:w-auto px-6 py-3 rounded-full font-bold text-[14px] tracking-wide whitespace-nowrap"
                        style={{ animationDelay: "0.91s" }}
                    >
                        {t("hero.ctaDiscord")}
                    </a>
                </div>

                {/* Stats row */}
                <div
                    className="fade-up mt-16 md:mt-20 flex flex-wrap items-center justify-center gap-6 md:gap-12 text-center"
                    style={{ animationDelay: "1.1s" }}
                    data-testid="hero-stats"
                >
                    {[
                        ["wipe", t("hero.statWipe"), t("hero.statWipeValue")],
                        ["uptime", t("hero.statUptime"), "99.9%"],
                        ["slots", t("hero.statSlots"), "100"],
                    ].map(([id, k, v]) => (
                        <div key={id} className="min-w-[90px]">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-zrs-metal mb-1">
                                {k}
                            </div>
                            <div className="text-gold text-base md:text-lg font-bold">
                                {v}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* bottom fade */}
            <div
                className="pointer-events-none absolute bottom-0 inset-x-0 h-40 z-[1]"
                style={{
                    background:
                        "linear-gradient(to bottom, transparent, #050505 90%)",
                }}
            />
        </section>
    );
}
